import { Button } from "@/components/flexkit/Button";
import { Tooltip } from "@/components/Tooltip";
import { useMemo, useEffect, forwardRef } from "react";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useListMCPIntegrations } from "@/queries/integration-queries";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import { IconIntegrations } from "@/assets/icons/geist/IconIntegrations";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { NotificationDot } from "@/components/flexkit/badge/NotificationDot";
import { isErrorSuppressed, type MCPErrorSnapshot } from "@/queries/integration-queries";

type MCPIntegrationsButtonProps = {
    agentExecution: PlainAgentExecution;
    organizationId?: string;
    errorSnapshots: Map<string, MCPErrorSnapshot>;
    // Destructured from ...rest so Radix's PopoverTrigger onClick is placed on
    // the outer container, making the warning indicator also open the popover.
    onClick?: React.MouseEventHandler<HTMLElement>;
} & PropsWithClassName;

export const MCPIntegrationsButton = forwardRef<HTMLButtonElement, MCPIntegrationsButtonProps>(
    function MCPIntegrationsButton(
        { className, agentExecution, organizationId, errorSnapshots, onClick, ...rest },
        ref,
    ) {
        const { data: integrations } = useListMCPIntegrations(organizationId);
        const { data: authTokensData } = useListHostAuthenticationTokens({ enabled: true });
        // Snapshot the statusVersion and authTokenId for each integration with errors.
        // Only update when statusVersion changes so we can detect stale errors after re-auth.
        const statusVersion = String(agentExecution.status?.statusVersion ?? "0");
        useEffect(() => {
            if (!integrations || !authTokensData || !agentExecution.status?.mcpIntegrationStatuses) return;

            const mcpStatuses = agentExecution.status.mcpIntegrationStatuses;
            const tokensMap = new Map(
                authTokensData.tokens.filter((t) => t.integrationId).map((t) => [t.integrationId, t]),
            );

            for (const integration of integrations) {
                const status = mcpStatuses.find((s) => s.id === integration.id);
                const hasError = !!(status?.failureMessage || status?.warningMessage);
                if (hasError) {
                    const existing = errorSnapshots.get(integration.id);
                    if (existing?.statusVersion !== statusVersion) {
                        errorSnapshots.set(integration.id, {
                            statusVersion,
                            authTokenId: tokensMap.get(integration.id)?.id,
                        });
                    }
                }
            }
        }, [
            integrations,
            authTokensData,
            agentExecution.status?.mcpIntegrationStatuses,
            statusVersion,
            errorSnapshots,
        ]);

        // Count integrations that have auth tokens and non-suppressed errors/warnings.
        // Pure computation — no side effects.
        const issueCount = useMemo(() => {
            if (!integrations || !authTokensData || !agentExecution.status?.mcpIntegrationStatuses) return 0;

            const mcpStatuses = agentExecution.status.mcpIntegrationStatuses;
            const tokensMap = new Map(
                authTokensData.tokens.filter((t) => t.integrationId).map((t) => [t.integrationId, t]),
            );

            return integrations.filter((integration) => {
                const status = mcpStatuses.find((s) => s.id === integration.id);
                const hasAuthToken = tokensMap.has(integration.id);
                const hasError = !!(status?.failureMessage || status?.warningMessage);
                if (!hasAuthToken || !hasError) return false;

                // Hack: Suppress stale errors: authToken changed but statusVersion hasn't
                if (
                    isErrorSuppressed(
                        errorSnapshots.get(integration.id),
                        statusVersion,
                        tokensMap.get(integration.id)?.id,
                    )
                ) {
                    return false;
                }

                return true;
            }).length;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [integrations, authTokensData, agentExecution.status?.mcpIntegrationStatuses, statusVersion]);

        return (
            <div className="relative flex items-center" onClick={onClick} data-tracking-id="mcp-integrations-area">
                <Tooltip content={issueCount > 0 ? "Some integrations need your attention" : "MCP Integrations"}>
                    <Button
                        ref={ref}
                        variant="ghost"
                        size="sm"
                        LeadingIcon={IconIntegrations}
                        className={cn("relative text-content-secondary", className)}
                        aria-label={
                            issueCount > 0 ? `MCP Integrations — ${issueCount} need attention` : "MCP Integrations"
                        }
                        data-tracking-id="mcp-integrations-button"
                        {...rest}
                    />
                </Tooltip>
                {issueCount > 0 && (
                    <NotificationDot className="right-[3px] top-[3px]" data-tracking-id="mcp-integrations-warning" />
                )}
            </div>
        );
    },
);
